import { routes } from "../../assets/constants";
import { ApiMethod } from "../models/api-types.type";
import { useAuthContext } from "./use-auth-context.hook";

export type InvitationDelivery = "email" | "link";
export type InvitationStatus = "pending" | "accepted" | "expired" | "revoked";

export interface Invitation {
    _id: string;
    delivery: InvitationDelivery;
    email?: string;
    role: string;
    vendor: { _id: string; name?: string } | null;
    invitedBy: string;
    maxUses: number;
    uses: number;
    status: InvitationStatus;
    expiresAt: string;
    lastSentAt?: string;
    createdAt?: string;
    /** Who created an account from it. */
    acceptedBy?: { _id: string; email: string; name?: string }[];
    revokedAt?: string;
    revokedBy?: string;
}

/** Why an email didn't go out: no SMTP on the server (retrying can't help), or the send failed. */
export type EmailError = "not_configured" | "failed";

export interface CreateInvitationInput {
    delivery: InvitationDelivery;
    email?: string;
    role: string;
    vendor?: string;
    maxUses?: number;
}

/** Super admin: invitations, and reset links for existing users. */
export const useInvitationsApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();

    return {
        createInvitation: (input: CreateInvitationInput): Promise<{ invitation: Invitation; link?: string; emailSent?: boolean; emailError?: EmailError }> =>
            sendAuthGuardedRequest(ApiMethod.POST, routes.invitations.base, input) as Promise<any>,
        listInvitations: (status: "pending" | "all" = "all"): Promise<Invitation[]> =>
            sendAuthGuardedRequest(ApiMethod.GET, `${routes.invitations.base}?status=${status}`) as Promise<any>,
        resendInvitation: (id: string): Promise<{ invitation: Invitation; emailSent: boolean; emailError?: EmailError }> =>
            sendAuthGuardedRequest(ApiMethod.POST, routes.invitations.resend(id)) as Promise<any>,
        revokeInvitation: (id: string): Promise<Invitation> =>
            sendAuthGuardedRequest(ApiMethod.POST, routes.invitations.revoke(id)) as Promise<any>,
        sendPasswordReset: (userId: string): Promise<{ emailSent: boolean; emailError?: EmailError }> =>
            sendAuthGuardedRequest(ApiMethod.POST, routes.account.adminPasswordReset(userId)) as Promise<any>,
    };
};
